<template>
  <span class="actuals-help-wrap">
    <b-button
      size="sm"
      variant="success"
      title="About the Multiday On-Time Report"
      aria-label="Help"
      @click="show_help = true"
    >
      <i class="fa fa-info-circle"></i>
    </b-button>

    <b-modal
      v-model="show_help"
      title="Multiday On-Time Report"
      size="xl"
      ok-only
      scrollable
    >
      <div class="actuals-help-body">
        <h4>What this page shows</h4>
        <p>
          One row per booking across the selected date range, comparing the <b>planned</b> times on the vehicle
          assignment against the <b>actual</b> times from actuals processing. Four milestones, each with Planned,
          Actual, and Diff columns, plus a Total Diff for the shift as a whole.
        </p>
        <ul>
          <li>Rows are selected by the day the trip <b>starts</b>, within the range you pick.</li>
          <li>Only <b>single-day</b> bookings are included (day 1 of 1). Later days of multi-day trips are skipped.</li>
          <li>Only bookings whose actuals processing <b>succeeded</b> appear — not cancelled, no processing errors, and with at least one actual time calculated.</li>
          <li>Nothing loads until you press <b>Run</b>. Changing the base clears the results and you'll need to run again.</li>
          <li>If your company has more than 15 yards, pick a <b>Base</b> before running — the range query is too broad otherwise.</li>
          <li>Times display in the originating yard's timezone.</li>
        </ul>

        <hr/>

        <h4>Where the Actual times come from</h4>
        <p>
          This page is split down the middle — the outer two columns are the <b>driver</b>, the inner two are the
          <b>vehicle</b>:
        </p>
        <ul>
          <li><b>Driver Start Time</b> and <b>Driver Finish Time</b> come from the driver's HOS clock-in and clock-out in telematics.</li>
          <li><b>Vehicle Departure Time</b> and <b>Vehicle Arrival Time</b> come from vehicle telematics (GPS) — the GPS-confirmed departure from the start location and arrival at the finish location.</li>
        </ul>
        <p>
          That pairing is the point of the page: it shows the gap between when the driver went on duty and when the
          coach actually rolled, and the same at the other end of the day. A driver who clocks in on time but leaves
          the yard 40 minutes late shows an on-time Driver Start and a late Vehicle Departure.
        </p>
        <p>
          <b>Total Diff</b> is the actual shift length minus the planned shift length, measured driver-to-driver
          (HOS clock-in to clock-out against planned driver start to end). It stays blank unless both the clock-in
          and clock-out were found.
        </p>
        <p class="actuals-help-note">
          Note the difference from the Daily On-Time Report: that page's six columns are all vehicle GPS and use no
          HOS at all. Use this page for "did the driver's day match the plan", and the daily one for stop-by-stop
          vehicle timing.
        </p>

        <hr/>

        <h4>Column by column</h4>
        <table class="actuals-help-col-table">
          <tr>
            <th>Column</th>
            <th>Planned</th>
            <th>Actual</th>
          </tr>
          <tr>
            <td><b>Driver Start Time</b></td>
            <td>Planned driver start on the assignment.</td>
            <td>Driver HOS clock-in.</td>
          </tr>
          <tr>
            <td><b>Vehicle Departure Time</b></td>
            <td>Planned departure from the start location.</td>
            <td>Vehicle GPS departure from the start location.</td>
          </tr>
          <tr>
            <td><b>Vehicle Arrival Time</b></td>
            <td>Planned arrival back at the finish location.</td>
            <td>Vehicle GPS arrival at the finish location.</td>
          </tr>
          <tr>
            <td><b>Driver Finish Time</b></td>
            <td>Planned driver end on the assignment.</td>
            <td>Driver HOS clock-out.</td>
          </tr>
        </table>

        <hr/>

        <h4>Diff columns</h4>
        <ul>
          <li><b>Diff</b> is Actual minus Planned, in minutes. Positive means the milestone happened <b>after</b> planned (late); negative means <b>before</b> planned (early).</li>
          <li>Shading here is not threshold-based: any negative Diff is green and any positive Diff is red, however small. The thresholds below drive the chips and the Days filter, not the cell colors.</li>
        </ul>

        <hr/>

        <h4>Thresholds and the Days filter</h4>
        <ul>
          <li><b>Total Diff</b>, <b>Start Diff</b>, and <b>Finish Diff</b> set how many minutes count as a meaningful gap. A row "meets" a threshold when the absolute Diff is at or beyond it.</li>
          <li><b>Days</b> and <b>Consecutive Days</b> are applied on the server when you Run. With Days set above 1, a row survives only if its vehicle (or driver, depending on the mode) has at least that many <i>consecutive</i> days in the range where the Total, Start, <b>and</b> Finish diffs all meet their thresholds — the pattern that points at a schedule that's wrong every day rather than one bad trip.</li>
          <li>In that mode the table is grouped, with a header row for each vehicle or driver.</li>
          <li>Changing a threshold re-filters the rows already loaded. Changing Days requires another Run, since the server does that pass.</li>
        </ul>

        <hr/>

        <h4>Filters</h4>
        <ul>
          <li>The first chip shows <b>rows displayed / rows loaded</b>. Click it to clear the chip filter.</li>
          <li><b>Diff — Total / Start / Finish</b> narrow to rows where that particular diff meets its threshold. Total additionally requires both driver actual times and a Total Diff value.</li>
          <li><b>Timing — On Time / Early / Late</b> are based on the <b>driver</b> times only; the vehicle columns don't affect them. Early means the flagged driver time(s) were early only, Late means late only — or both directions on the same row, which counts as Late.</li>
          <li><b>Type</b> narrows to a booking type; <b>Driver</b> narrows to one driver.</li>
          <li>Click any column heading to sort by it; click again to reverse.</li>
        </ul>
        <p class="actuals-help-note">
          <b>Download</b> applies the three thresholds as hard filters, so the CSV can hold fewer rows than the
          screen shows with no chip selected — on screen those thresholds only drive the chips.
        </p>

        <hr/>

        <h4>Links out</h4>
        <ul>
          <li><b>Booking ID</b> — opens the Actuals page for that booking.</li>
          <li><b>Vehicle</b> — opens that vehicle's trip history for the trip's start day.</li>
          <li><b>Driver</b> — opens that driver's HOS report for the day (Samsara companies only), which is the source behind the two driver columns.</li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
export default {
  name: 'actuals_multiday_otp_help_tbn',

  data() {
    return {
      show_help: false,
    }
  },
}
</script>

<style scoped>
.actuals-help-wrap {
  display: inline-block;
  margin-left: 8px;
}
.actuals-help-body {
  font-size: 15px;
  line-height: 1.5;
  text-align: left;
}
.actuals-help-body h4 {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
}
.actuals-help-body p {
  margin-bottom: 10px;
}
.actuals-help-body ul {
  padding-left: 20px;
  margin-bottom: 10px;
}
.actuals-help-body li {
  margin-bottom: 6px;
}
.actuals-help-note {
  font-size: 14px;
  font-style: italic;
  color: #6c757d;
}
.actuals-help-col-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}
.actuals-help-col-table th,
.actuals-help-col-table td {
  border: 1px solid #ddd;
  padding: 6px 8px;
  vertical-align: top;
  font-size: 14px;
}
.actuals-help-col-table th {
  background-color: #f1f3f5;
  text-align: left;
}
.actuals-help-col-table td:first-child {
  white-space: nowrap;
}
</style>
